import React, { useEffect, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Button, IconButton, Popover, Stack, Typography } from "@mui/material";
import { PsychologyReverse } from "assets/material-custom-icons";
import { AiDataTable } from "components/AiData/AiDataTable";
import { AiDataContext } from "components/AiData/context/AiData-context";
import { useAiDataContextHook } from "components/AiData/context/use-AIData-context-hook";
import { PopoverContent } from "components/AiData/PopoverContent";
import { UploadFilesContent } from "components/AiData/UploadFilesContent";
import { AsyncContentMui } from "components/Async-content/AsyncContentMui";
import { AiProcessingContent } from "components/modals/Files-categorization-modal/AiProcessedContent";
import { useActionHook, useNavigate, usePrompt } from "hooks";
import { useDataRoomsActions, useDealActions } from "store";
import { IK1TableData, IRecommendationData } from "store/types/data-rooms";
import pickFiles from "utils/pick-files";

import { Empty } from "./Empty";
import { K1Table } from "./K1Table";
import { Views } from "./Views";

export type ViewType = "tools" | "history" | "k1";

interface ViewContentProps {
  view: ViewType;
  recommendationsTable: IRecommendationData[];
  k1TableData: IK1TableData[];
  isK1TableLoading: boolean;
  onFileUpload: (preselectedFileList?: FileList) => void;
}

const ViewContent: React.FC<ViewContentProps> = ({
  view,
  recommendationsTable,
  k1TableData,
  isK1TableLoading,
  onFileUpload
}) => {
  if (view === "tools") {
    return <Empty onFileUpload={onFileUpload} />;
  }

  if (view === "history") {
    return recommendationsTable?.length ? (
      <Stack sx={{ maxHeight: 400 }}>
        <AiDataTable hidePagination />
      </Stack>
    ) : (
      <Empty onFileUpload={onFileUpload} />
    );
  }

  if (view === "k1") {
    return k1TableData.length ? (
      <K1Table data={k1TableData} isLoading={isK1TableLoading} />
    ) : (
      <Empty onFileUpload={onFileUpload} />
    );
  }

  return null;
};

export const Extract: React.FC = () => {
  const [view, setView] = useState<ViewType>("tools");
  const aiDataContext = useAiDataContextHook({ defaultPerPage: 200 });
  const { recommendationsTable, isRecommendationsTableLoading, getTable } = aiDataContext;

  const { uploadFilesWithAiAction } = useDealActions();
  const { getK1TableAction } = useDataRoomsActions();
  const { action: getK1Table, loading: isK1TableLoading, data: k1TableData = [] } = useActionHook(getK1TableAction);
  const openPrompt = usePrompt();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isRecommendationsTableLoading && recommendationsTable.length) {
      setView("history");
    }
  }, [isRecommendationsTableLoading]);

  useEffect(() => {
    if (view === "k1") {
      getK1Table();
    }
    if (view === "history") {
      getTable({ page: 1, per_page: 200 });
    }
  }, [view]);

  // -------- Upload files ---------
  const onFileUpload = async (preselectedFileList?: FileList) => {
    const fileList =
      preselectedFileList ||
      (await pickFiles({
        multiple: true,
        accept: "application/pdf"
      }));

    if (!fileList?.length) return;

    const result = await openPrompt({
      Component: UploadFilesContent,
      props: {
        files: fileList
      }
    });

    if (result) {
      try {
        await openPrompt({
          Component: AiProcessingContent,
          props: {
            title: "Upload files",
            type: "aiCategorization",
            applyButtonText: "Apply",
            cb: async () => {
              const promises = Object.entries(result).map(([dealId, files]) => {
                return uploadFilesWithAiAction({
                  dealId: Number(dealId),
                  files: files,
                  aiExtraction: true,
                  ...(view === "k1" && { isK1Extraction: true })
                });
              });
              await Promise.all(promises);
            }
          }
        });
      } catch (error) {
        console.error(error);
      }
    }
  };

  return (
    <AiDataContext.Provider value={aiDataContext}>
      <Stack
        sx={{
          p: "16px",
          gap: "16px",
          bgcolor: "white",
          borderRadius: "8px",
          boxShadow: "xs"
        }}
      >
        <Stack sx={{ flexDirection: "row", alignItems: "center", justifyContent: "space-between" }}>
          <Stack sx={{ flexDirection: "row", alignItems: "center", gap: "10px" }}>
            <PsychologyReverse sx={{ fontSize: "24px", fill: "#0A3142" }} />
            <Typography variant="h6" sx={{ color: "#0A3142" }}>
              Extract AI
            </Typography>
          </Stack>
          {!isRecommendationsTableLoading && (
            <Button
              size="sm"
              onClick={() => onFileUpload()}
              startIcon={<AddIcon sx={{ color: "#0A3142" }} />}
              variant="outlined"
            >
              Upload File
            </Button>
          )}
        </Stack>

        <Stack sx={{ flexDirection: "row", alignItems: "center", gap: "8px", mb: "16px" }}>
          <Views value={view} onChange={setView} />
          <IconButton sx={{ width: "20px", height: "20px", padding: 0 }} onClick={() => navigate({ route: "aiData" })}>
            <OpenInNewIcon sx={{ width: "16px", height: "16px", color: "#071F35" }} />
          </IconButton>
        </Stack>
        <AsyncContentMui
          boxProps={{ sx: { minHeight: 275 } }}
          isLoading={view === "history" ? isRecommendationsTableLoading : view === "k1" ? isK1TableLoading : false}
        >
          <ViewContent
            key={view}
            view={view}
            recommendationsTable={recommendationsTable}
            k1TableData={k1TableData}
            isK1TableLoading={isK1TableLoading}
            onFileUpload={onFileUpload}
          />
        </AsyncContentMui>
      </Stack>
      <Popover
        open={Boolean(aiDataContext.openedCell?.anchorEl)}
        onClose={() => aiDataContext.setOpenedCell(null)}
        anchorEl={aiDataContext.openedCell?.anchorEl}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left"
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "left"
        }}
        sx={{
          "& .MuiPopover-paper": {
            marginTop: "8px",
            width: "430px"
          }
        }}
      >
        <PopoverContent
          openedCell={aiDataContext.openedCell}
          setOpenedCell={aiDataContext.setOpenedCell}
          onChangeSpecificRecommendationValue={aiDataContext.onChangeSpecificRecommendationValue}
        />
      </Popover>
    </AiDataContext.Provider>
  );
};
